import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useDeviceStore } from '../../stores/device-store';

// Port of src/app/directives/is-in-view.directive.ts ([isInView]).
//
// Directive behavior (mirrored exactly):
// - IntersectionObserver with rootMargin '80px 0px' on mobile, '40px 0px' otherwise
//   (mobile detection came from MobileDeviceService -> useDeviceStore here).
// - Fires ONCE per element: on first intersection it unobserves the target.
// - Inside requestAnimationFrame it adds an animation class to the element based
//   on the entry's boundingClientRect: 'zoom-in-right-blur' when the element's
//   left edge is in the left half of the screen, 'zoom-in-left-blur' otherwise
//   (classes are defined in styles.scss under .pop-animate).
// - The directive emitted no @Output; `onInView` is offered as a React-friendly
//   extra so callers can react to the (single) intersection.

export interface UseInViewOptions {
  /** Called once, when the element first intersects (after the animation class is applied). */
  onInView?: (entry: IntersectionObserverEntry) => void;
  /**
   * Replicates the directive's class manipulation (adds 'zoom-in-right-blur' /
   * 'zoom-in-left-blur'). Defaults to true — set false to only track state.
   */
  applyAnimationClass?: boolean;
}

export function useInView<T extends HTMLElement>(
  options?: UseInViewOptions
): { ref: RefObject<T | null>; isInView: boolean } {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  // Keep latest options without re-creating the observer.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    let rafId = 0;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          rafId = requestAnimationFrame(() => {
            const opts = optionsRef.current;
            if (opts?.applyAnimationClass !== false) {
              const halfScreen = window.innerWidth / 2;
              if (entry.boundingClientRect.left < halfScreen) {
                entry.target.classList.add('zoom-in-right-blur');
              } else {
                entry.target.classList.add('zoom-in-left-blur');
              }
            }
            observer.unobserve(entry.target); // Ensure the animation only occurs once
            setIsInView(true);
            opts?.onInView?.(entry);
          });
        }
      });
    }, {
      rootMargin: useDeviceStore.getState().isMobileDevice ? '80px 0px' : '40px 0px'
    });

    observer.observe(element);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  return { ref, isInView };
}
